import React, {useState} from "react"
import styled, {css} from "styled-components"

import {connect} from "@irati/store"
import authentication from "@irati/auth"
import {isNewPoll} from "@irati/recognition/utils/polls/isNew"
import FormattedMessage, {FormattedRelative} from "@irati/utils/lang"
import id from "@irati/utils/id"
import {TextButton} from "@irati/ui/src/Button"
import {POLL} from "@irati/recognition/constants/feedTypes"
import CompanyValues from "@irati-web/companyvalues/src/components/CompanyValuesList"

import {FeedItem} from "../../Feed"
import {getPollPath, getPollPublishPath} from "../../../utils/paths"
import {RouterLink, FlexRow as FlexRowBase, BodyContainer as BodyContainerBase} from "../../shared"
import WhoCreated from "../../WhoCreated"
import ProfilePicGroup, {OUTER_WRAPPER_SIZE, SINGLE_PICTURE_SIZE, MULTIPLE_PICTURE_SIZE} from "../../ProfilePicGroup"
import {DEFAULT_TROPHY_SIZE, MOBILE_TROPHY_SIZE} from "../../ProfilePicWithTrophy"
import {Activity as IconsActivity} from "../../Icons"
import {AddReaction} from "../../Reactions"
import OccasionIcon from "../../shared/OccasionIcon"
import Confetti from "./Confetti"
import {IconsRow} from "./styled"
import {Poll as PollTitle} from "./Titles"
import {EndDate, StyledBody} from "./styled/Card"
import {Poll, PollStatus} from "@irati/types"
import {Body} from "@irati/ui/src/Typography"
import mainTheme from "@irati/ui/src/global/theme"
import {ButtonRouterLink} from "@irati/ui/src/Link"

const FlexRow = styled(FlexRowBase)`
  align-items: start;
`

const BodyContainer = styled(BodyContainerBase)`
  ${({theme}) => css`
    margin-left: ${theme.spacing.one};
    ${theme.mediaQueries.minWidthMedia.tablet`
      margin-left: ${theme.spacing.four};
    `}
  `}
`
interface Props {
  item: {
    poll: Poll
  }
  onClick: () => void
  isMobile: boolean
  className?: string
  id?: string
  showPublish?: boolean
}
const FeedPoll = (props: Props) => {
  const {
    className,
    onClick,
    item: {poll},
    isMobile,
    id: feedItemId,
    showPublish
  } = props
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const togglePickerModal = () => setShowEmojiPicker(!showEmojiPicker)
  const handleClick = () => onClick(poll)
  const getButtonProps = () => ({
    inline: false,
    onClick: handleClick,
    id: id("btn", props)
  })

  const getLinkProps = () => ({
    to: getPollPath(poll),
    scrollToTop: false,
    plain: true
  })
  const handleShowModal = (ev) => {
    ev.stopPropagation()
    ev.preventDefault()
  }

  const users = poll.winners.results.map((w) => w.user)
  const user = authentication.user()
  const isNew = isNewPoll(poll)
  const isWinner = users.map(({uuid}) => uuid).includes(user.uuid)

  const hasClickCallback = typeof onClick === "function"
  const Button = hasClickCallback ? TextButton : RouterLink
  const btnProps = hasClickCallback ? getButtonProps() : getLinkProps()
  const isAuthor = user.uuid === poll.createdBy?.uuid

  return (
    <>
      <Button {...btnProps}>
        {showEmojiPicker && <AddReaction feedItem item={poll} togglePickerModal={togglePickerModal} />}
        <FeedItem className={className} isNew={isNew}>
          {isNew && isWinner && <Confetti />}
          <FlexRow>
            <ProfilePicGroup
              users={users}
              trophy={poll.trophy}
              trophySize={isMobile ? MOBILE_TROPHY_SIZE : DEFAULT_TROPHY_SIZE}
              outerWrapperSize={OUTER_WRAPPER_SIZE(isMobile)}
              singlePictureSize={SINGLE_PICTURE_SIZE(isMobile)}
              multiplePictureSize={MULTIPLE_PICTURE_SIZE(isMobile)}
            />
            <BodyContainer>
              <PollTitle poll={poll} />
              <IconsRow>
                <OccasionIcon occasion={poll.occasion} />
                <CompanyValues companyValues={poll.companyValues} limit={1} />
              </IconsRow>
              <EndDate>
                <FormattedRelative value={poll.endsAt} />
              </EndDate>
              <StyledBody small marginBottom={1}>
                {poll.desc}
              </StyledBody>
              <WhoCreated createdBy={poll.createdBy} />
              <IconsActivity feedItemId={feedItemId} item={poll} type={POLL} togglePickerModal={togglePickerModal} />
              {showPublish && isAuthor && poll.status === PollStatus.ClosedUnpublished && (
                <ButtonRouterLink to={getPollPublishPath(poll)} primary marginTop={2} stopPropagation>
                  <FormattedMessage id="platform.recognition.polls.btn.closedPollsPublish" />
                </ButtonRouterLink>
              )}
              {showPublish && poll.status === PollStatus.Closed && (
                <Body small weight="bold" color={mainTheme.colors.primary2} marginTop={2}>
                  <FormattedMessage id="platform.recognition.polls.lbl.published" />
                </Body>
              )}
            </BodyContainer>
          </FlexRow>
        </FeedItem>
      </Button>
    </>
  )
}

const mapStateToProps = ({mq}) => {
  return {
    isMobile: mq.lessThan.md
  }
}

export default connect(mapStateToProps)(FeedPoll)
